import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Upload,
  FileText,
  Sparkles,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Edit3,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import { api } from "../lib/api";

const STORAGE_KEY = "cfobooks_email_v1";

// Sample data for /cfo-books/dashboard?demo=1 — lets us show the product to
// signups + future investors without exposing real data or requiring preorder.
const DEMO_TRANSACTIONS = [
  { id: "d1",  date: "2026-02-28", description: "Stripe payout",                amount:  12_840.50, category: "revenue",          user_overridden: false },
  { id: "d2",  date: "2026-02-27", description: "Square daily settlement",      amount:   3_215.10, category: "revenue",          user_overridden: false },
  { id: "d3",  date: "2026-02-27", description: "Gusto payroll run",            amount: -18_452.00, category: "payroll",          user_overridden: false },
  { id: "d4",  date: "2026-02-25", description: "Sysco Foods inv #4421",        amount:  -9_812.34, category: "cogs",             user_overridden: true  },
  { id: "d5",  date: "2026-02-25", description: "Rent — 217 Main St",           amount:  -4_500.00, category: "rent",             user_overridden: false },
  { id: "d6",  date: "2026-02-24", description: "ConEd electric",               amount:    -638.22, category: "utilities",        user_overridden: false },
  { id: "d7",  date: "2026-02-22", description: "Facebook Ads",                 amount:  -1_240.00, category: "marketing",        user_overridden: false },
  { id: "d8",  date: "2026-02-22", description: "Notion subscription",          amount:     -96.00, category: "software",         user_overridden: false },
  { id: "d9",  date: "2026-02-21", description: "Customer payment — Acme Co",   amount:   8_750.00, category: "revenue",          user_overridden: false },
  { id: "d10", date: "2026-02-20", description: "ADP payroll tax remittance",   amount:  -3_124.00, category: "payroll",          user_overridden: false },
  { id: "d11", date: "2026-02-18", description: "Uber — Q1 sales trip",         amount:    -184.55, category: "travel",           user_overridden: false },
  { id: "d12", date: "2026-02-17", description: "AmEx Annual Card Fee",         amount:    -250.00, category: "uncategorized",    user_overridden: false },
  { id: "d13", date: "2026-02-15", description: "Refund — defective shipment",  amount:    -612.00, category: "uncategorized",    user_overridden: false },
  { id: "d14", date: "2026-02-14", description: "Stripe payout",                amount:   9_120.75, category: "revenue",          user_overridden: false },
  { id: "d15", date: "2026-02-12", description: "AT&T Business",                amount:    -218.00, category: "utilities",        user_overridden: false },
  { id: "d16", date: "2026-02-10", description: "Office Depot — supplies",      amount:    -163.40, category: "office_supplies",  user_overridden: false },
  { id: "d17", date: "2026-02-08", description: "Loan payment #14",             amount:  -2_850.00, category: "loan_principal",   user_overridden: false },
  { id: "d18", date: "2026-02-05", description: "Chase wire fee",               amount:     -35.00, category: "bank_fees",        user_overridden: false },
  { id: "d19", date: "2026-02-03", description: "Liability insurance",          amount:    -412.00, category: "insurance",        user_overridden: false },
  { id: "d20", date: "2026-02-01", description: "Stripe payout",                amount:  14_280.00, category: "revenue",          user_overridden: false },
];

const DEMO_PERIOD = computeDemoPeriod(DEMO_TRANSACTIONS);

function computeDemoPeriod(txns) {
  const totals = {};
  for (const t of txns) {
    totals[t.category] = (totals[t.category] || 0) + t.amount;
  }
  const revenue = (totals.revenue || 0) + (totals.other_income || 0);
  const cogs = Math.abs(totals.cogs || 0);
  const gross_profit = revenue - cogs;
  const op_keys = [
    "payroll", "rent", "utilities", "marketing", "software",
    "insurance", "professional_fees", "travel",
    "meals_entertainment", "office_supplies",
  ];
  const operating_costs = op_keys.reduce((s, k) => s + Math.abs(totals[k] || 0), 0);
  const operating_income = gross_profit - operating_costs;
  return {
    period: "2026-02",
    tx_count: txns.length,
    ready: true,
    summary: {
      revenue: Math.round(revenue * 100) / 100,
      cogs: Math.round(cogs * 100) / 100,
      gross_profit: Math.round(gross_profit * 100) / 100,
      operating_costs: Math.round(operating_costs * 100) / 100,
      operating_income: Math.round(operating_income * 100) / 100,
    },
  };
}

const DEMO_COUNTS = DEMO_TRANSACTIONS.reduce((acc, t) => {
  acc[t.category] = (acc[t.category] || 0) + 1;
  return acc;
}, {});

const CATEGORY_LABELS = {
  revenue: "Revenue",
  cogs: "COGS",
  payroll: "Payroll",
  rent: "Rent",
  utilities: "Utilities",
  marketing: "Marketing",
  software: "Software",
  insurance: "Insurance",
  professional_fees: "Professional fees",
  travel: "Travel",
  meals_entertainment: "Meals & entertainment",
  office_supplies: "Office supplies",
  bank_fees: "Bank fees",
  interest_expense: "Interest expense",
  loan_principal: "Loan principal",
  owner_draw: "Owner draw",
  transfer: "Transfer",
  tax: "Tax",
  other_expense: "Other expense",
  other_income: "Other income",
  uncategorized: "Uncategorized",
};

const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS);

export default function CFOBooksDashboardPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isDemo = params.get("demo") === "1";
  const [email, setEmail] = useState(() => {
    return params.get("email") || localStorage.getItem(STORAGE_KEY) || "";
  });
  const [emailDraft, setEmailDraft] = useState("");
  const [access, setAccess] = useState(null); // null = unknown, {has_access, reason}
  const [statements, setStatements] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [counts, setCounts] = useState({});
  const [period, setPeriod] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    document.title = isDemo ? "CFO Books — Live demo" : "Your CFO Books — Dashboard";
    if (isDemo) {
      // Hydrate from in-memory sample data; no backend calls.
      setAccess({ has_access: true, reason: "demo", email: "demo@cfograde.com" });
      setEmail("demo@cfograde.com");
      setTransactions(DEMO_TRANSACTIONS);
      setCounts(DEMO_COUNTS);
      setPeriod(DEMO_PERIOD);
      return;
    }
    if (email) {
      localStorage.setItem(STORAGE_KEY, email);
      checkAccess(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  async function checkAccess(emailToCheck) {
    try {
      const r = await api.get("/cfo-books/access", { params: { email: emailToCheck } });
      setAccess(r.data);
      if (r.data.has_access) {
        await loadData(emailToCheck);
      }
    } catch {
      setAccess({ has_access: false, reason: "error" });
    }
  }

  async function loadData(emailToLoad) {
    try {
      const r = await api.get("/cfo-books/transactions", {
        params: { email: emailToLoad, limit: 500 },
      });
      setTransactions(r.data.items || []);
      setCounts(r.data.by_category || {});

      // Pull current month period summary
      const now = new Date();
      const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const p = await api.get(`/cfo-books/period/${ym}`, { params: { email: emailToLoad } });
      setPeriod(p.data);
    } catch (err) {
      toast.error("Couldn't load your CFO Books data.");
    }
  }

  function handleEmailSubmit(e) {
    e.preventDefault();
    if (!emailDraft.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    const normalized = emailDraft.trim().toLowerCase();
    setEmail(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
    checkAccess(normalized);
  }

  async function handleUpload(file) {
    if (!file) return;
    if (isDemo) {
      toast.info("This is a live demo. Upload disabled — reserve your spot at /cfo-books to upload real statements.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (!email) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/cfo-books/upload?email=${encodeURIComponent(email)}&bank_hint=stub`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const { state, transactions_extracted } = r.data;
      if (state === "needs_manual_review") {
        toast.info(
          "Got it. Our automated parser is still in beta — we've queued your statement and will email you the extracted books within 24h."
        );
      } else {
        toast.success(`Extracted ${transactions_extracted} transactions.`);
      }
      await loadData(email);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed.";
      toast.error(detail);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function recategorize(txnId, newCat) {
    if (isDemo) {
      // In-memory only; demonstrates the UX without server hit
      setTransactions((prev) =>
        prev.map((t) => (t.id === txnId ? { ...t, category: newCat, user_overridden: true } : t))
      );
      toast.success("Demo: in production this trains future categorization.");
      return;
    }
    try {
      await api.post(
        `/cfo-books/transactions/${txnId}/recategorize?email=${encodeURIComponent(email)}`,
        { category: newCat }
      );
      // Optimistic update
      setTransactions((prev) =>
        prev.map((t) => (t.id === txnId ? { ...t, category: newCat, user_overridden: true } : t))
      );
      toast.success("Saved. Thanks — your correction trains future categorization.");
    } catch (err) {
      toast.error("Couldn't update. Try again.");
    }
  }

  // --- Gate: no email entered yet ---
  if (!email) {
    return (
      <Shell>
        <div className="max-w-md mx-auto pt-16 text-center" data-testid="cfo-books-dashboard-gate">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C26D5C] bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-full px-3 py-1.5">
            <Sparkles size={12} strokeWidth={1.5} /> CFO Books · Private beta
          </div>
          <h1 className="font-serif-display text-4xl text-[#1F382E] leading-tight mt-5 tracking-tight">
            Sign in to your CFO Books.
          </h1>
          <p className="text-[#5C665D] mt-4 leading-relaxed">
            Enter the email you used to reserve your spot.
          </p>
          <form onSubmit={handleEmailSubmit} className="mt-6 space-y-3">
            <Input
              type="email"
              required
              placeholder="you@business.com"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              className="h-11 bg-white border-[#E5E0D8]"
              data-testid="cfo-books-email-input"
            />
            <Button
              type="submit"
              className="w-full bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11"
              data-testid="cfo-books-email-submit"
            >
              Continue
            </Button>
          </form>
          <p className="text-xs text-[#5C665D] mt-5">
            Don't have a spot yet?{" "}
            <Link to="/cfo-books" className="underline underline-offset-2 hover:text-[#1F382E]">
              Reserve with $1 →
            </Link>
          </p>
        </div>
      </Shell>
    );
  }

  // --- Loading access check ---
  if (access === null) {
    return (
      <Shell>
        <div className="max-w-md mx-auto pt-16 text-center">
          <Loader2 size={24} className="animate-spin text-[#5C665D] mx-auto" />
          <p className="text-sm text-[#5C665D] mt-3">Checking your access…</p>
        </div>
      </Shell>
    );
  }

  // --- No access ---
  if (!access.has_access) {
    return (
      <Shell>
        <div className="max-w-md mx-auto pt-16 text-center" data-testid="cfo-books-no-access">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#C26D5C]/10 text-[#C26D5C]">
            <Lock size={26} strokeWidth={1.5} />
          </div>
          <h1 className="font-serif-display text-3xl text-[#1F382E] mt-5 tracking-tight">
            {access.reason === "not_on_list"
              ? "We don't see that email."
              : "You're on the free waitlist."}
          </h1>
          <p className="text-[#5C665D] mt-3 leading-relaxed">
            {access.reason === "not_on_list"
              ? "Reserve your spot with a $1 deposit to unlock CFO Books early access."
              : "CFO Books early access requires the $1 deposit. Free waitlisters get launch-day access at full price."}
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Link to="/cfo-books">
              <Button className="w-full bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11">
                Reserve with $1 →
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(STORAGE_KEY);
                setEmail("");
                setAccess(null);
              }}
              className="text-xs text-[#5C665D] hover:text-[#1F382E] underline underline-offset-2 mt-2"
              data-testid="switch-email-btn"
            >
              Use a different email
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // --- Authenticated dashboard ---
  const uncategorizedCount = counts.uncategorized || 0;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-2 pb-20" data-testid="cfo-books-dashboard">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
              <Sparkles size={12} strokeWidth={1.5} className="text-[#C26D5C]" />
              CFO Books · Private beta
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-tight mt-3 tracking-tight">
              Your books, automated.
            </h1>
            <p className="text-[#5C665D] mt-3">
              Signed in as <strong className="text-[#1F382E]">{email}</strong>{" "}
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem(STORAGE_KEY);
                  setEmail("");
                  setAccess(null);
                }}
                className="text-xs underline underline-offset-2 hover:text-[#1F382E] ml-2"
              >
                switch
              </button>
            </p>
          </div>
        </div>

        {/* Beta-phase / demo banner */}
        {isDemo ? (
          <div className="bg-[#2C5C43]/8 border border-[#2C5C43]/30 rounded-xl p-5 mb-8" data-testid="demo-banner">
            <div className="flex items-start gap-3">
              <Sparkles size={18} strokeWidth={1.5} className="text-[#2C5C43] mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-[#1F382E]">You're viewing a live demo with sample data.</p>
                <p className="text-sm text-[#5C665D] mt-1 leading-relaxed">
                  Try clicking any category to see how AI categorizations can be overridden. Numbers
                  are fictional. When CFO Books launches, your real bank statements feed this same
                  dashboard.
                </p>
                <Link to="/cfo-books" className="inline-block mt-3">
                  <Button
                    className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-9 px-4 text-sm"
                    data-testid="demo-cta-reserve"
                  >
                    Reserve your spot →
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-[#C26D5C]/8 border border-[#C26D5C]/30 rounded-xl p-5 mb-8" data-testid="beta-banner">
            <div className="flex items-start gap-3">
              <AlertCircle size={18} strokeWidth={1.5} className="text-[#C26D5C] mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-[#1F382E]">CFO Books is in private beta.</p>
                <p className="text-sm text-[#5C665D] mt-1 leading-relaxed">
                  Our automated bank-statement parser is being built — for now, uploads queue for our
                  manual review (we'll get your reconstructed books within 24h). Your $1 deposit holds
                  your founding-member rate of $79/mo for 12 months.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT — Upload + summary */}
          <aside className="lg:col-span-1 space-y-6">
            {/* Upload widget */}
            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="upload-widget">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                Upload a bank statement
              </p>
              <h2 className="font-serif-display text-xl text-[#1F382E] mt-2 tracking-tight">
                Drop a statement PDF
              </h2>
              <p className="text-xs text-[#5C665D] mt-2 leading-relaxed">
                PDF, up to 20 MB. We support most US, UK, and Canadian bank formats.
              </p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="w-full mt-4 border-2 border-dashed border-[#E5E0D8] hover:border-[#1F382E]/40 rounded-xl py-8 px-4 flex flex-col items-center gap-2 transition-all duration-300 text-[#5C665D] hover:text-[#1F382E] hover:bg-[#F7F5F0]"
                data-testid="upload-dropzone"
              >
                {busy ? (
                  <>
                    <Loader2 size={24} strokeWidth={1.5} className="animate-spin" />
                    <span className="text-xs">Uploading…</span>
                  </>
                ) : (
                  <>
                    <Upload size={24} strokeWidth={1.5} />
                    <span className="text-sm font-medium">Choose file</span>
                  </>
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="upload-input"
              />
            </div>

            {/* This month's summary */}
            {period && (
              <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="period-summary">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                  This month · {period.period}
                </p>
                {period.ready ? (
                  <ul className="mt-4 space-y-2 text-sm">
                    {Object.entries(period.summary).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between border-b border-[#F0EDE5] last:border-0 py-1.5">
                        <span className="text-[#5C665D] capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="font-medium text-[#1F382E] tabular-nums">
                          ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-[#5C665D] mt-3 leading-relaxed">
                    No transactions yet this month. Upload a statement to see your reconstructed P&L.
                  </p>
                )}
              </div>
            )}
          </aside>

          {/* RIGHT — Transactions review */}
          <section className="lg:col-span-2">
            <div className="bg-white border border-[#E5E0D8] rounded-2xl" data-testid="transactions-panel">
              <div className="px-6 py-5 border-b border-[#E5E0D8] flex items-end justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                    Your transactions
                  </p>
                  <h2 className="font-serif-display text-xl text-[#1F382E] mt-1 tracking-tight">
                    {transactions.length === 0
                      ? "Nothing here yet"
                      : `${transactions.length} extracted`}
                  </h2>
                </div>
                {uncategorizedCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-[#C26D5C]/10 text-[#C26D5C] border border-[#C26D5C]/30 rounded-full px-3 py-1">
                    <AlertCircle size={11} strokeWidth={1.75} />
                    {uncategorizedCount} need review
                  </span>
                )}
              </div>
              {transactions.length === 0 ? (
                <div className="px-6 py-16 text-center text-[#5C665D]">
                  <FileText size={32} strokeWidth={1.3} className="mx-auto opacity-50" />
                  <p className="font-serif-display text-lg text-[#1F382E] mt-4">
                    Upload your first statement
                  </p>
                  <p className="text-sm text-[#5C665D] mt-2 max-w-sm mx-auto">
                    Once we've extracted your transactions, they'll show up here for you to review
                    and reclassify.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                        <th className="py-3 px-5 font-medium">Date</th>
                        <th className="py-3 px-5 font-medium">Description</th>
                        <th className="py-3 px-5 font-medium text-right">Amount</th>
                        <th className="py-3 px-5 font-medium">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 100).map((t) => (
                        <tr key={t.id} className="border-b border-[#F0EDE5] last:border-0 hover:bg-[#F7F5F0]/50">
                          <td className="py-2.5 px-5 text-[#5C665D] whitespace-nowrap">{t.date}</td>
                          <td className="py-2.5 px-5 text-[#1A1A1A] truncate max-w-[260px]">
                            {t.description}
                          </td>
                          <td className={`py-2.5 px-5 text-right tabular-nums font-medium ${t.amount >= 0 ? "text-[#2C5C43]" : "text-[#1A1A1A]"}`}>
                            ${Math.abs(t.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2.5 px-5">
                            <CategorySelect
                              value={t.category}
                              overridden={t.user_overridden}
                              onChange={(v) => recategorize(t.id, v)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </Shell>
  );
}

function CategorySelect({ value, overridden, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`text-xs rounded-md border px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#1F382E]/20 ${
          value === "uncategorized"
            ? "border-[#C26D5C]/40 text-[#C26D5C]"
            : "border-[#E5E0D8] text-[#1F382E]"
        }`}
        data-testid={`category-select-${value}`}
      >
        {CATEGORY_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {CATEGORY_LABELS[opt]}
          </option>
        ))}
      </select>
      {overridden && (
        <Edit3
          size={10}
          strokeWidth={1.5}
          className="text-[#5C665D]"
          aria-label="Manually adjusted"
        />
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
        </Link>
      </header>
      {children}
      <SiteFootnote />
    </div>
  );
}
